import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

function StatTile({ label, value }) {
  return (
    <div className="bg-white border border-[#E2E4E2] p-5">
      <div className="text-xs uppercase tracking-[0.2em] text-[#68706B] font-semibold">{label}</div>
      <div className="text-3xl font-bold mt-2 text-[#1E3F33]">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("flagged");
  const [professions, setProfessions] = useState([]);
  const [newProf, setNewProf] = useState("");

  const load = async () => {
    try {
      const [s, v, r, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/videos", { params: { status: filter } }),
        api.get("/admin/reports"),
        api.get("/professions"),
      ]);
      setStats(s.data); setVideos(v.data); setReports(r.data); setProfessions(p.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (vid, action) => {
    const fd = new FormData(); fd.append("action", action);
    if (action === "reject") {
      const reason = window.prompt("Reason for rejection (shown to the candidate):");
      if (reason === null) return;
      fd.append("reason", reason);
    }
    try {
      await api.post(`/admin/videos/${vid}/action`, fd);
      toast.success(action);
      load();
    } catch { toast.error("Failed"); }
  };

  const addProfession = async () => {
    if (!newProf.trim()) return;
    try {
      await api.post("/admin/professions", { name: newProf.trim() });
      setNewProf("");
      toast.success("Profession added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteProfession = async (p) => {
    if (!confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/professions/${p.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-end justify-between">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#D96C5B] font-semibold">Control room</span>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Admin moderation</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6" data-testid="admin-stats">
          <StatTile label="Users" value={stats.users} />
          <StatTile label="Candidates" value={stats.candidates} />
          <StatTile label="Recruiters" value={stats.hrs} />
          <StatTile label="Videos" value={stats.videos} />
          <StatTile label="Flagged" value={stats.flagged} />
          <StatTile label="Reports" value={stats.reports_open} />
        </div>
      )}

      <Tabs defaultValue="videos" className="mt-8">
        <TabsList className="rounded-none bg-[#E8ECEB]">
          <TabsTrigger value="videos" className="rounded-none" data-testid="tab-videos">Videos</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-none" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="professions" className="rounded-none" data-testid="tab-professions">Professions</TabsTrigger>
        </TabsList>

        <TabsContent value="videos" className="mt-4">
          <div className="flex gap-2 mb-4">
            {["flagged", "approved", "rejected"].map((s) => (
              <Button key={s} variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-none ${filter === s ? "bg-[#1E3F33]" : ""}`} data-testid={`filter-${s}`}>{s}</Button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-videos">
            {videos.length === 0 && <div className="col-span-full text-sm text-[#68706B] p-6">Nothing here.</div>}
            {videos.map((v) => (
              <div key={v.id} className="bg-white border border-[#E2E4E2]">
                <div className="aspect-video bg-black"><video src={fileUrl(v.video_file_id)} className="w-full h-full object-cover" controls muted /></div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-widest text-[#D96C5B] font-semibold">{v.moderation_status}</div>
                  {v.moderation_reason && <div className="text-xs text-[#68706B] mt-1">{v.moderation_reason}</div>}
                  <div className="font-bold mt-2">{v.title}</div>
                  <div className="text-xs text-[#68706B]">by {v.user_name} • {v.user_sector || "—"}</div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => act(v.id, "approve")} className="rounded-none bg-[#1E3F33]" data-testid={`approve-${v.id}`}>Approve</Button>
                    <Button size="sm" onClick={() => act(v.id, "reject")} variant="outline" className="rounded-none border-[#D96C5B] text-[#D96C5B]" data-testid={`reject-${v.id}`}>Reject</Button>
                    <Button size="sm" onClick={() => act(v.id, "delete")} variant="outline" className="rounded-none" data-testid={`delete-${v.id}`}>Delete</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <div className="bg-white border border-[#E2E4E2] divide-y divide-[#E2E4E2]" data-testid="admin-reports">
            {reports.length === 0 && <div className="p-6 text-sm text-[#68706B]">No reports.</div>}
            {reports.map((r) => (
              <div key={r.id} className="p-4 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div><div className="text-xs uppercase tracking-widest text-[#68706B]">Video</div><div className="font-mono text-xs">{r.video_id.slice(0, 8)}…</div></div>
                <div className="md:col-span-2"><div className="text-xs uppercase tracking-widest text-[#68706B]">Reason</div>{r.reason}</div>
                <div><div className="text-xs uppercase tracking-widest text-[#68706B]">Status</div>{r.status}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="professions" className="mt-4">
          <div className="bg-white border border-[#E2E4E2] p-5">
            <div className="flex gap-2 max-w-md">
              <Input value={newProf} onChange={(e) => setNewProf(e.target.value)} placeholder="New profession category" className="rounded-none h-10" onKeyDown={(e) => e.key === "Enter" && addProfession()} data-testid="new-profession-input" />
              <Button onClick={addProfession} className="rounded-none bg-[#1E3F33] h-10" data-testid="add-profession-btn">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-5" data-testid="admin-professions">
              {professions.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-[#F0EFEB] border border-[#E2E4E2]">
                  {p.name}
                  <button onClick={() => deleteProfession(p)} className="text-[#D96C5B] hover:text-[#B85948]" data-testid={`delete-prof-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
